import { FC, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Modal } from 'src/components';
import { useUserContext } from 'src/context/user';
import { SessionSourceType } from 'src/api/learningSessions/types';
import { TrackingApi } from 'src/api';
import { MemMarkdown } from '../MemMarkdown';
import * as S from './styles';

const msgs = defineMessages({
  didYouKnow: {
    id: 'learn_presentationcard_nugget_title',
    defaultMessage: 'Did you know?',
  },
  ok: {
    id: 'learnq_messaging_existing_users_CTA',
    defaultMessage: 'OK',
  },
  learnMore: {
    id: 'learn_presentationcard_nugget_learn_more',
    defaultMessage: 'LEARN MORE',
  },
});

export interface NuggetProps {
  nugget?: string;
  languagePairId: number;
  sessionSourceType: SessionSourceType;
  sessionSourceId: number;
  expanded?: boolean;
  /** Lighten text/background for embedding in a dark video overlay panel. */
  overlay?: boolean;
}

export const Nugget: FC<NuggetProps> = ({
  nugget,
  languagePairId,
  sessionSourceType,
  sessionSourceId,
  expanded = false,
  overlay = false,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Only show if nugget content exists
  if (!nugget) {
    return null;
  }

  const handleOpenModal = () => {
    setIsModalOpen(true);

    void TrackingApi.trackUserAction(
      'clicked_open_nugget',
      'learning_session',
      languagePairId,
      !!user?.isPro,
      {
        page_screen_type: 'Learning session',
        page_screen_name: 'Presentation card',
        session_source_type: sessionSourceType,
        session_source_id: String(sessionSourceId),
        click_area: 'Did you know button',
      },
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <S.NuggetCard data-testid="nugget-button" $overlay={overlay}>
        <S.NuggetHeader>
          <S.NuggetEmoji>🤓</S.NuggetEmoji>
          <S.NuggetTitle $overlay={overlay}>{intl.formatMessage(msgs.didYouKnow)}</S.NuggetTitle>
        </S.NuggetHeader>
        <S.NuggetPreview $expanded={expanded} $overlay={overlay}>
          <MemMarkdown text={nugget} />
        </S.NuggetPreview>
        {!expanded && (
          <S.LearnMoreLink $overlay={overlay} onClick={handleOpenModal}>
            {intl.formatMessage(msgs.learnMore)}
          </S.LearnMoreLink>
        )}
      </S.NuggetCard>

      <Modal
        isOpen={isModalOpen}
        handleClose={handleCloseModal}
        shouldCloseOnOutsideClick
        shouldCloseOnEsc
        darkCloseIcon
      >
        <S.ModalContent data-testid="nugget-modal">
          <S.ModalTitle>{intl.formatMessage(msgs.didYouKnow)}</S.ModalTitle>
          <S.NuggetText>
            <MemMarkdown text={nugget} />
          </S.NuggetText>
          <S.ButtonContainer>
            <Button
              buttonType="primary"
              buttonSize="medium"
              onClick={handleCloseModal}
              data-testid="nugget-ok-button"
            >
              {intl.formatMessage(msgs.ok)}
            </Button>
          </S.ButtonContainer>
        </S.ModalContent>
      </Modal>
    </>
  );
};
